import { TypedAnalyticsEvent } from "@omni/types/components/tracking/analyticsEvent";
import { CartImpressionTrackingInfoItem } from "@omni/types/components/tracking/tracking-info/impression/cartImpression.trackingInfo";
import { CheckoutToolStepTrackingInfo } from "@omni/types/components/tracking/tracking-info/tools/toolStep.trackingInfo";

import * as AlgoliaTrackingService from "@/services/algolia/algoliaTracking.service";
import * as AlgoliaUserService from "@/services/algolia/algoliaUser.service";
import { Config } from "@/utils/config.utils";

import {
    AlgoliaAnalyticsEventNames,
    AlgoliaAnalyticsEvents
} from "../AlgoliaAnalyticsEvents";
import {
    AlgoliaAnalyticsMinimumDataset,
    AlgoliaObjectData,
    AlgoliaPurchasePayload
} from "../AlgoliaAnalyticsMinimumDataset";

export interface AlgoliaAnalyticsPurchaseImpression
    extends AlgoliaAnalyticsMinimumDataset<AlgoliaPurchasePayload> {}

const mapEventDataToAlgoliaObjectData = (
    cartItems: Array<CartImpressionTrackingInfoItem>
): Array<AlgoliaObjectData & { sku: string }> => {
    return cartItems.map((ci) => ({
        price: ci.unitPrice,
        quantity: ci.quantity,
        queryID: AlgoliaTrackingService.getLastQueryTrackRecordFor(ci.id)
            ?.queryID,
        sku: ci.id
    }));
};

const getAlgoliaPurchasePayloads = (
    eventData: CheckoutToolStepTrackingInfo
): Array<AlgoliaAnalyticsPurchaseImpression> => {
    const objectData = mapEventDataToAlgoliaObjectData(eventData.cart.items);

    const objectsWithQueryId = objectData.filter((d) => !!d.queryID);
    const objectsWithoutQueryId = objectData.filter((d) => !d.queryID);

    const payloads: Array<AlgoliaAnalyticsPurchaseImpression> = [];

    if (objectsWithQueryId.length > 0) {
        // afterSearch
        payloads.push({
            eventName: AlgoliaAnalyticsEvents.PurchaseAfterSearch,
            payload: {
                authenticatedUserToken:
                    AlgoliaUserService.getAuthenticatedUserToken(),
                currency: "EUR",
                eventName: AlgoliaAnalyticsEventNames.PurchaseAfterSearch,
                index: Config.integrations["algolia-index-product"],
                userToken: AlgoliaUserService.getUserToken(),
                objectIDs: objectsWithQueryId.map((o) => o.sku),
                objectData: objectsWithQueryId.map((o) => {
                    const { sku: _, ...data } = o;
                    return data;
                })
            }
        });
    }

    if (objectsWithoutQueryId.length > 0) {
        // nosearch
        payloads.push({
            eventName: AlgoliaAnalyticsEvents.PurchaseNoSearch,
            payload: {
                authenticatedUserToken:
                    AlgoliaUserService.getAuthenticatedUserToken(),
                currency: "EUR",
                eventName: AlgoliaAnalyticsEventNames.Purchase,
                index: Config.integrations["algolia-index-product"],
                userToken: AlgoliaUserService.getUserToken(),
                objectIDs: objectsWithoutQueryId.map((o) => o.sku),
                objectData: objectsWithoutQueryId.map((o) => {
                    const { sku: _, ...data } = o;
                    return data;
                })
            }
        });
    }

    return payloads;
};

const getAlgoliaAnalyticsPurchaseImpression = (
    data: TypedAnalyticsEvent<CheckoutToolStepTrackingInfo>
): Array<AlgoliaAnalyticsPurchaseImpression> => {
    const payloads = getAlgoliaPurchasePayloads(data.payload.eventData);

    data.payload.eventData.cart.items
        .map((i) => i.id)
        .forEach(AlgoliaTrackingService.removeQueryTrackRecordFor);

    return payloads;
};

export { getAlgoliaAnalyticsPurchaseImpression };
